import type { StateStorage } from 'zustand/middleware';
import { reportTelemetryError } from '@terra/ui-telemetry';
import type { ReplaySession } from './replaySessionStore';
import type {
  ReplayCollection,
  ReplaySession as ReplayStoreSession,
  ConnectionTarget,
} from '../types/replay';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import type { BodyEncoding } from '../electron-shared/utils/binary-body';

const TENANT_ID_KEY = 'terra-proxy-current-tenant-id';

/**
 * Get the current tenant ID. When `authTenantId` is passed (e.g. from gateway),
 * it takes precedence over localStorage cache (TenantSwitcher / initTenantIdFromJwt).
 */
export function getCurrentTenantId(
  authTenantId?: string | null,
): string | null {
  if (typeof window === 'undefined') {
    return authTenantId ?? null;
  }

  return authTenantId ?? localStorage.getItem(TENANT_ID_KEY);
}

/**
 * Initialize tenant ID from JWT on app startup.
 * Returns the tenant ID if found, null otherwise.
 */
export async function initTenantIdFromJwt(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  const cachedTenantId = getCurrentTenantId();

  if (window.electronAPI?.auth?.getCurrentTenantId) {
    try {
      const result = await window.electronAPI.auth.getCurrentTenantId();

      if (result.success && result.tenantId) {
        if (result.tenantId !== cachedTenantId) {
          setCurrentTenantId(result.tenantId);
        }

        return result.tenantId;
      }
    } catch (error) {
      console.warn('[tenantStorage] Failed to get tenant ID from JWT:', error);
    }
  }

  return cachedTenantId;
}

/**
 * Set the current tenant ID in cache.
 * Called after login or tenant switch.
 */
export function setCurrentTenantId(tenantId: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TENANT_ID_KEY, tenantId);
}

/**
 * Clear the cached tenant ID.
 * Called on logout.
 */
export function clearCurrentTenantId(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TENANT_ID_KEY);
}

/**
 * Creates a tenant-aware storage key.
 * Returns null if no tenant ID is set (prevents premature hydration).
 * Format: {baseName}-{tenantId}
 */
export function getTenantStorageKey(baseName: string): string | null {
  const tenantId = getCurrentTenantId();

  if (!tenantId) {
    return null;
  }

  return `${baseName}-${tenantId}`;
}

function reportTenantStorageSetItemFailure({
  error,
  key,
  value,
}: {
  error: unknown;
  key: string;
  value: string;
}): void {
  console.warn(
    `[tenantStorage] Failed to persist "${key}"; keeping in-memory state only`,
    error,
  );
  reportTelemetryError(error, {
    errorType: 'TenantStorageSetItem',
    key,
    valueLength: value.length,
  });
}

/**
 * Tenant-aware localStorage adapter for zustand persist.
 * Automatically prefixes storage keys with current tenant ID.
 * Returns null/no-ops when tenant ID is not set (prevents premature hydration).
 *
 * Usage:
 * ```
 * persist(
 *   (set, get) => ({ ... }),
 *   {
 *     name: 'terra-proxy-my-store', // base name
 *     storage: createJSONStorage(() => createTenantStorage()),
 *   }
 * )
 * ```
 */
export function createTenantStorage(): StateStorage {
  return {
    getItem: (name: string): string | null => {
      if (typeof window === 'undefined') return null;
      const key = getTenantStorageKey(name);

      if (!key) return null;

      return localStorage.getItem(key);
    },
    setItem: (name: string, value: string): void => {
      if (typeof window === 'undefined') return;
      const key = getTenantStorageKey(name);

      if (!key) return;

      try {
        localStorage.setItem(key, value);
      } catch (error: unknown) {
        reportTenantStorageSetItemFailure({ error, key, value });
      }
    },
    removeItem: (name: string): void => {
      if (typeof window === 'undefined') return;
      const key = getTenantStorageKey(name);

      if (!key) return;
      localStorage.removeItem(key);
    },
  };
}

/**
 * Tenant-aware IndexedDB storage adapter for zustand persist.
 * For stores that need larger storage capacity.
 * Returns null/no-ops when tenant ID is not set (prevents premature hydration).
 *
 * Usage:
 * ```
 * persist(
 *   (set, get) => ({ ... }),
 *   {
 *     name: 'terra-proxy-my-store',
 *     storage: createJSONStorage(() => createTenantIndexedDBStorage()),
 *   }
 * )
 * ```
 */
export function createTenantIndexedDBStorage(): StateStorage {
  return {
    getItem: async (name: string): Promise<string | null> => {
      const key = getTenantStorageKey(name);

      if (!key) return null;
      const { get } = await import('idb-keyval');
      const value = await get<string>(key);

      return value ?? null;
    },
    setItem: async (name: string, value: string): Promise<void> => {
      const key = getTenantStorageKey(name);

      if (!key) return;

      try {
        const { set } = await import('idb-keyval');

        await set(key, value);
      } catch (error: unknown) {
        reportTenantStorageSetItemFailure({ error, key, value });
      }
    },
    removeItem: async (name: string): Promise<void> => {
      const key = getTenantStorageKey(name);

      if (!key) return;
      const { del } = await import('idb-keyval');

      await del(key);
    },
  };
}

type PersistedResponseHistoryItem = {
  statusCode: number;
  statusMessage: string;
  headers: Record<string, string | string[]>;
  body: string;
  duration: number;
  error?: string;
  timestamp: string | Date;
  requestData: string;
  credential?: {
    id: string;
    username: string;
    role: string;
  };
  webGrpcDecodedData?: WebGrpcDecodedData | null;
  sentRequestWire?: string;
  sentRequestWireEncoding?: BodyEncoding;
};

type PersistedReplaySession = Omit<
  ReplaySession,
  'responseHistory' | 'connectionTarget'
> & {
  collectionId?: string;
  responseHistory: PersistedResponseHistoryItem[];
  connectionTarget?: ConnectionTarget;
};

export type PersistedReplaySessionState = {
  collections?: ReplayCollection[];
  sessions?: PersistedReplaySession[];
  activeSessionId?: string | null;
  activeCollectionId?: string;
  collapsedCollectionIds?: string[];
};

// Types for replay-store.ts Date revival
type PersistedReplayRequest = {
  id: string;
  name: string;
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  body: string;
  timestamp: string | Date;
  originalRequestId?: string;
};

type PersistedReplayResponse = {
  id: string;
  requestId: string;
  statusCode: number;
  statusMessage: string;
  headers: Record<string, string | string[]>;
  body: string;
  timestamp: string | Date;
  duration: number;
  error?: string;
};

type PersistedReplayStoreSession = {
  id: string;
  name: string;
  collectionId: string;
  requests: PersistedReplayRequest[];
  responses: PersistedReplayResponse[];
  currentRequestIndex: number;
  createdAt: string | Date;
  updatedAt: string | Date;
};

type PersistedReplayCollection = {
  id: string;
  name: string;
  description?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export type PersistedReplayStoreState = {
  collections?: PersistedReplayCollection[];
  sessions?: PersistedReplayStoreSession[];
};

/**
 * Revives Date objects from persisted state.
 * Converts date strings back to Date objects in nested structures.
 */
export function reviveDates(
  state: PersistedReplaySessionState | null | undefined,
): Partial<{
  collections: ReplayCollection[];
  sessions: ReplaySession[];
  activeSessionId: string | null;
  activeCollectionId: string;
  collapsedCollectionIds: string[];
}> {
  if (!state) {
    return {};
  }

  const result: {
    collections?: ReplayCollection[];
    sessions?: ReplaySession[];
    activeSessionId?: string | null;
    activeCollectionId?: string;
    collapsedCollectionIds?: string[];
  } = {};

  if (state.activeSessionId !== undefined) {
    result.activeSessionId = state.activeSessionId;
  }

  if (state.activeCollectionId !== undefined) {
    result.activeCollectionId = state.activeCollectionId;
  }

  if (
    state.collapsedCollectionIds &&
    Array.isArray(state.collapsedCollectionIds)
  ) {
    result.collapsedCollectionIds = state.collapsedCollectionIds;
  }

  if (state.collections && Array.isArray(state.collections)) {
    result.collections = state.collections.map((collection) => ({
      ...collection,
      createdAt:
        typeof collection.createdAt === 'string'
          ? new Date(collection.createdAt)
          : collection.createdAt,
      updatedAt:
        typeof collection.updatedAt === 'string'
          ? new Date(collection.updatedAt)
          : collection.updatedAt,
    }));
  }

  if (state.sessions && Array.isArray(state.sessions)) {
    result.sessions = state.sessions.map((session): ReplaySession => {
      let connectionTarget = session.connectionTarget;

      if (!connectionTarget && session.request?.url) {
        try {
          const url = new URL(session.request.url);

          connectionTarget = {
            hostname: url.hostname,
            port: parseInt(url.port) || (url.protocol === 'https:' ? 443 : 80),
            https: url.protocol === 'https:',
          };
        } catch {
          connectionTarget = { hostname: 'localhost', port: 80, https: false };
        }
      }

      if (!connectionTarget) {
        connectionTarget = { hostname: 'localhost', port: 80, https: false };
      }

      return {
        ...session,
        connectionTarget,
        responseHistory: session.responseHistory.map((response) => {
          return {
            ...response,
            timestamp:
              typeof response.timestamp === 'string'
                ? new Date(response.timestamp)
                : response.timestamp,
          };
        }),
      };
    });
  }

  return result;
}

/**
 * Helper to convert a date string or Date to a Date object.
 */
function toDate(value: string | Date): Date {
  return typeof value === 'string' ? new Date(value) : value;
}

type RevivedReplayStoreState = {
  collections?: ReplayCollection[];
  sessions?: ReplayStoreSession[];
};

/**
 * Revives Date objects from persisted replay store state.
 * Converts date strings back to Date objects for collections, sessions, requests, and responses.
 */
export function reviveReplayStoreDates(
  state: PersistedReplayStoreState | null | undefined,
): RevivedReplayStoreState {
  if (!state) {
    return {};
  }

  const result: RevivedReplayStoreState = {};

  if (state.collections && Array.isArray(state.collections)) {
    result.collections = state.collections.map((collection) => ({
      ...collection,
      createdAt: toDate(collection.createdAt),
      updatedAt: toDate(collection.updatedAt),
    }));
  }

  if (state.sessions && Array.isArray(state.sessions)) {
    result.sessions = state.sessions.map((session) => ({
      ...session,
      createdAt: toDate(session.createdAt),
      updatedAt: toDate(session.updatedAt),
      requests: session.requests.map((request) => ({
        ...request,
        timestamp: toDate(request.timestamp),
      })),
      responses: session.responses.map((response) => ({
        ...response,
        timestamp: toDate(response.timestamp),
      })),
    }));
  }

  return result;
}
